import * as PIXI from 'pixi.js';

export type EndingType = 'victory' | 'gameOver';

export interface EndingScreenOptions {
  type: EndingType;
  title?: string;
  caption?: string;
  onRestart?: () => void;
}

export class EndingScreen {
  private container: PIXI.Container;
  private overlay: PIXI.Graphics;
  private titleText: PIXI.Text;
  private captionText: PIXI.Text;
  private restartButton: PIXI.Container;
  private restartButtonBackground: PIXI.Graphics;
  private restartButtonText: PIXI.Text;
  private isVisible: boolean = false;
  private screenWidth: number;
  private screenHeight: number;
  private onRestartCallback?: () => void;

  constructor(screenWidth: number, screenHeight: number) {
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;

    // Create main container
    this.container = new PIXI.Container();
    this.container.visible = false;
    this.container.zIndex = 1000; // Ensure it's on top

    // Create spooky dark overlay with gradient effect
    this.overlay = new PIXI.Graphics();
    this.overlay.beginFill(0x0a0a0a, 0.95); // Very dark background
    this.overlay.drawRect(0, 0, screenWidth, screenHeight);
    this.overlay.endFill();
    this.overlay.interactive = true; // Prevent clicks from going through
    this.container.addChild(this.overlay);

    // Create spooky title text
    this.titleText = new PIXI.Text('', {
      fontFamily: 'serif',
      fontSize: 84,
      fontWeight: 'bold',
      fill: 0xffffff,
      align: 'center',
      dropShadow: {
        color: 0x6b46c1, // Purple shadow
        angle: Math.PI / 4,
        distance: 6,
        blur: 15,
        alpha: 1,
      },
      stroke: {
        color: 0x1a1a2e,
        width: 2,
      },
    });
    this.titleText.anchor.set(0.5);
    this.container.addChild(this.titleText);

    // Create spooky caption text
    this.captionText = new PIXI.Text('', {
      fontFamily: 'serif',
      fontSize: 28,
      fill: 0xd4d4d8, // Light gray
      align: 'center',
      wordWrap: true,
      wordWrapWidth: Math.min(600, screenWidth * 0.8),
      fontStyle: 'italic',
      dropShadow: {
        color: 0x7c3aed, // Purple shadow
        angle: Math.PI / 6,
        distance: 2,
        blur: 4,
        alpha: 1,
      },
    });
    this.captionText.anchor.set(0.5);
    this.container.addChild(this.captionText);

    // Create restart button
    this.restartButton = new PIXI.Container();
    this.restartButton.interactive = true;
    this.restartButton.cursor = 'pointer';

    // Restart button background
    this.restartButtonBackground = new PIXI.Graphics();
    this.restartButton.addChild(this.restartButtonBackground);

    // Spooky restart button text
    this.restartButtonText = new PIXI.Text('Restart', {
      fontFamily: 'serif',
      fontSize: 32,
      fontWeight: 'bold',
      fill: 0xe2e8f0, // Light purple-gray
      align: 'center',
      dropShadow: {
        color: 0x7c3aed,
        angle: Math.PI / 6,
        distance: 2,
        blur: 6,
        alpha: 1,
      },
    });
    this.restartButtonText.anchor.set(0.5);
    this.restartButton.addChild(this.restartButtonText);

    this.container.addChild(this.restartButton);

    // Set up button interactions
    this.setupButtonInteractions();

    // Position elements
    this.updateLayout();
  }

  private setupButtonInteractions(): void {
    // Mouse events with spooky colors
    this.restartButton.on('pointerover', () => {
      this.restartButtonBackground.clear();
      this.restartButtonBackground.beginFill(0x7c3aed, 1); // Purple on hover
      this.restartButtonBackground.drawRoundedRect(-120, -30, 240, 60, 15);
      this.restartButtonBackground.endFill();
      this.restartButton.scale.set(1.08);

      // Add glow effect
      this.restartButtonBackground.lineStyle(3, 0xa855f7, 0.8);
      this.restartButtonBackground.drawRoundedRect(-120, -30, 240, 60, 15);
    });

    this.restartButton.on('pointerout', () => {
      this.restartButtonBackground.clear();
      this.restartButtonBackground.beginFill(0x1a1a2e, 1); // Dark purple
      this.restartButtonBackground.drawRoundedRect(-120, -30, 240, 60, 15);
      this.restartButtonBackground.endFill();
      this.restartButton.scale.set(1);

      // Add subtle border
      this.restartButtonBackground.lineStyle(2, 0x6b46c1, 0.6);
      this.restartButtonBackground.drawRoundedRect(-120, -30, 240, 60, 15);
    });

    this.restartButton.on('pointerdown', () => {
      this.restartButton.scale.set(0.95);
    });

    this.restartButton.on('pointerup', () => {
      this.restartButton.scale.set(1.08);
      this.handleRestart();
    });

    // Keyboard support (Enter key)
    window.addEventListener('keydown', e => {
      if (this.isVisible && e.key === 'Enter') {
        e.preventDefault();
        this.handleRestart();
      }
    });

    // Initial spooky button style
    this.restartButtonBackground.beginFill(0x1a1a2e, 1);
    this.restartButtonBackground.drawRoundedRect(-120, -30, 240, 60, 15);
    this.restartButtonBackground.endFill();
    this.restartButtonBackground.lineStyle(2, 0x6b46c1, 0.6);
    this.restartButtonBackground.drawRoundedRect(-120, -30, 240, 60, 15);
  }

  private handleRestart(): void {
    if (this.onRestartCallback) {
      this.onRestartCallback();
    }
    this.hide();
  }

  public show(options: EndingScreenOptions): void {
    const {
      type,
      title = type === 'victory' ? '🎃 VICTORY! 🎃' : '💀 GAME OVER 💀',
      caption = type === 'victory'
        ? 'The spirits smile upon you... You have conquered the darkness!'
        : 'The shadows have claimed you... But death is not the end...',
      onRestart,
    } = options;

    this.onRestartCallback = onRestart;

    // Set title with spooky colors
    this.titleText.text = title;
    if (type === 'victory') {
      this.titleText.style.fill = 0x22c55e; // Bright green for victory
      this.titleText.style.dropShadow = {
        color: 0x15803d, // Darker green shadow
        angle: Math.PI / 4,
        distance: 6,
        blur: 15,
        alpha: 1,
      };
    } else {
      this.titleText.style.fill = 0xef4444; // Bright red for game over
      this.titleText.style.dropShadow = {
        color: 0x991b1b, // Darker red shadow
        angle: Math.PI / 4,
        distance: 6,
        blur: 15,
        alpha: 1,
      };
    }

    // Set caption
    this.captionText.text = caption;

    // Update word wrap width based on current screen size
    this.captionText.style.wordWrapWidth = Math.min(600, this.screenWidth * 0.8);

    // Update layout and show
    this.updateLayout();
    this.isVisible = true;
    this.container.visible = true;

    // Add fade-in animation
    this.container.alpha = 0;
    this.animateIn();
  }

  public hide(): void {
    this.isVisible = false;
    this.animateOut().then(() => {
      this.container.visible = false;
      this.onRestartCallback = undefined;
    });
  }

  private animateIn(): void {
    const startTime = Date.now();
    const duration = 500; // 500ms fade in

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease out animation
      const easeOut = 1 - (1 - progress) ** 3;
      this.container.alpha = easeOut;

      // Scale animation for title
      const scale = 0.5 + easeOut * 0.5;
      this.titleText.scale.set(Math.min(scale * 1.2, 1));

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.titleText.scale.set(1);
      }
    };

    requestAnimationFrame(animate);
  }

  private animateOut(): Promise<void> {
    return new Promise(resolve => {
      const startTime = Date.now();
      const duration = 300; // 300ms fade out

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Ease in animation
        const easeIn = progress ** 2;
        this.container.alpha = 1 - easeIn;

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };

      requestAnimationFrame(animate);
    });
  }

  private updateLayout(): void {
    const centerX = this.screenWidth / 2;
    const centerY = this.screenHeight / 2;

    // Position title
    this.titleText.position.set(centerX, centerY - 120);

    // Position caption
    this.captionText.position.set(centerX, centerY - 20);

    // Position restart button
    this.restartButton.position.set(centerX, centerY + 80);

    // Update spooky overlay with gradient effect
    this.overlay.clear();
    this.overlay.beginFill(0x0a0a0a, 0.95);
    this.overlay.drawRect(0, 0, this.screenWidth, this.screenHeight);
    this.overlay.endFill();
  }

  public updateScreenSize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;

    // Update caption word wrap
    this.captionText.style.wordWrapWidth = Math.min(600, width * 0.8);

    this.updateLayout();
  }

  public getContainer(): PIXI.Container {
    return this.container;
  }

  public isShowing(): boolean {
    return this.isVisible;
  }

  public destroy(): void {
    // Remove event listeners
    window.removeEventListener('keydown', this.handleRestart);

    // Destroy PIXI objects
    this.container.destroy({ children: true });
  }
}
